import * as React from 'react';
import {
    Checkbox,
    CommandBar,
    DefaultButton,
    DetailsList,
    DetailsListLayoutMode,
    Dialog,
    DialogFooter,
    DialogType,
    Dropdown,
    IDropdownOption,
    IColumn,
    ICommandBarItemProps,
    IconButton,
    Label,
    MessageBar,
    MessageBarType,
    Panel,
    PanelType,
    PrimaryButton,
    Selection,
    SelectionMode,
    Separator,
    Spinner,
    SpinnerSize,
    Stack,
    Text,
    TextField,
} from '@fluentui/react';
import { ApiHelper } from '../../../ApiHelper';
import { Environment, Process, Service } from '../../../interfaces/Service';
import {
    AgentTaskListDetailResult,
    AgentTaskSnapshotSummary,
    CreateSnapshotFormState,
    getDerivedMetricId,
    isProfileBackedTaskType,
    isUnsupportedTaskType,
    isUploadBackedTaskType,
    submitMultipartRequest,
    useProfileDates,
} from '../shared';

export function CreateSnapshotPanel(props: {
    isOpen: boolean;
    serviceName: string;
    service?: Service;
    detail: AgentTaskListDetailResult;
    onDismiss: () => void;
    onCreated: (result: AgentTaskSnapshotSummary) => void;
}) {
    const { isOpen, serviceName, service, detail, onDismiss, onCreated } = props;
    const latestSnapshot = detail.latestSnapshot ?? detail.snapshots[0];
    const [formState, setFormState] = React.useState<CreateSnapshotFormState>({
        name: '',
        environmentId: latestSnapshot?.environmentId,
        date: undefined,
        file: null,
    });
    const [submitting, setSubmitting] = React.useState(false);
    const [submitError, setSubmitError] = React.useState<string | null>(null);

    React.useEffect(() => {
        if (!isOpen) {
            return;
        }

        setFormState({
            name: '',
            environmentId: latestSnapshot?.environmentId,
            date: undefined,
            file: null,
        });
        setSubmitError(null);
    }, [isOpen, latestSnapshot]);

    const selectedProcess = React.useMemo(
        () => service?.Processes?.find((process: Process) => process.id === detail.taskList.processId),
        [detail.taskList.processId, service],
    );

    const selectedMetricId = React.useMemo(
        () => getDerivedMetricId(selectedProcess, detail.taskList.taskType),
        [detail.taskList.taskType, selectedProcess],
    );

    const { dates, loading: loadingDates, error: dateError } = useProfileDates(
        serviceName,
        isProfileBackedTaskType(detail.taskList.taskType),
        formState.environmentId,
        detail.taskList.processId,
        selectedMetricId,
    );

    React.useEffect(() => {
        if (!isProfileBackedTaskType(detail.taskList.taskType)) {
            return;
        }

        if (dates.length > 0 && (!formState.date || !dates.some(date => date.dateAsString === formState.date))) {
            setFormState(current => ({
                ...current,
                date: dates[0].dateAsString,
            }));
        }
    }, [dates, detail.taskList.taskType, formState.date]);

    async function handleSubmit() {
        setSubmitting(true);
        setSubmitError(null);

        try {
            if (isUnsupportedTaskType(detail.taskList.taskType)) {
                throw new Error('Memory monitored scopes is not implemented yet.');
            }

            if (isProfileBackedTaskType(detail.taskList.taskType)) {
                if (formState.environmentId === undefined || !formState.date) {
                    throw new Error('Environment and profile date are required.');
                }

                if (selectedMetricId === undefined) {
                    throw new Error('The selected process does not define the required profile metric for this task type.');
                }

                const result = await ApiHelper(`/api/service/${serviceName}/agenttasklists/${detail.taskList.taskListId}/snapshots`, 'POST', {
                    name: formState.name || undefined,
                    sourceKind: 0,
                    environmentId: formState.environmentId,
                    metricId: selectedMetricId,
                    date: formState.date,
                }) as AgentTaskSnapshotSummary;

                onCreated(result);
                return;
            }

            if (isUploadBackedTaskType(detail.taskList.taskType)) {
                if (!formState.file) {
                    throw new Error('Please select the master JSON file to upload.');
                }

                const result = await submitMultipartRequest<AgentTaskSnapshotSummary>(
                    `/api/service/${serviceName}/agenttasklists/${detail.taskList.taskListId}/snapshots`,
                    {
                        name: formState.name || undefined,
                        sourceKind: 1,
                    },
                    formState.file,
                );

                onCreated(result);
                return;
            }
        } catch (err) {
            setSubmitError(err instanceof Error ? err.message : 'Failed to create the snapshot.');
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <Panel
            isOpen={isOpen}
            onDismiss={onDismiss}
            type={PanelType.medium}
            headerText="Create snapshot"
        >
            <Stack tokens={{ childrenGap: 16 }}>
                {submitError && (
                    <MessageBar messageBarType={MessageBarType.error}>{submitError}</MessageBar>
                )}

                <TextField
                    label="Display name"
                    value={formState.name}
                    onChange={(_, value) => setFormState(current => ({ ...current, name: value ?? '' }))}
                />

                {isProfileBackedTaskType(detail.taskList.taskType) && (
                    <>
                        <Dropdown
                            label="Environment"
                            options={(service?.Environments ?? []).map((environment: Environment) => ({ key: environment.id, text: environment.name }))}
                            selectedKey={formState.environmentId}
                            onChange={(_, option) => setFormState(current => ({ ...current, environmentId: Number(option?.key) }))}
                        />

                        {selectedMetricId === undefined && (
                            <MessageBar messageBarType={MessageBarType.error}>
                                The selected process does not define the required profile metric for this task type.
                            </MessageBar>
                        )}

                        {dateError && (
                            <MessageBar messageBarType={MessageBarType.error}>{dateError}</MessageBar>
                        )}

                        <Dropdown
                            label="Profile date"
                            options={dates.map(date => ({ key: date.dateAsString, text: date.dateAsString }))}
                            selectedKey={formState.date}
                            onChange={(_, option) => setFormState(current => ({ ...current, date: String(option?.key) }))}
                            disabled={loadingDates || dates.length === 0}
                            placeholder={loadingDates ? 'Loading available profiles...' : 'Select a profile date'}
                        />
                    </>
                )}

                {isUploadBackedTaskType(detail.taskList.taskType) && (
                    <>
                        <Label>Master JSON file</Label>
                        <input
                            type="file"
                            accept=".json,application/json"
                            onChange={event => setFormState(current => ({
                                ...current,
                                file: event.target.files?.[0] ?? null,
                            }))}
                        />
                    </>
                )}

                <Stack horizontal tokens={{ childrenGap: 8 }}>
                    <PrimaryButton
                        text={submitting ? 'Creating...' : 'Create snapshot'}
                        onClick={handleSubmit}
                        disabled={submitting || isUnsupportedTaskType(detail.taskList.taskType)}
                    />
                    <DefaultButton text="Cancel" onClick={onDismiss} disabled={submitting} />
                </Stack>
            </Stack>
        </Panel>
    );
}



